import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";

// Layouts
import { PublicLayout } from "@/components/layout/PublicLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";

// Public Pages
import { SubmitTicketPage } from "@/pages/SubmitTicket";
import { TrackTicketPage } from "@/pages/TrackTicket";

// Admin Pages
import { LoginPage } from "@/pages/admin/Login";
import { ForgetPasswordPage } from "@/pages/admin/ForgetPassword";
import { ResetPasswordPage } from "@/pages/admin/ResetPassword";
import { ChangePasswordPage } from "@/pages/admin/ChangePassword";
import { DashboardPage } from "@/pages/admin/Dashboard";
import { KanbanPage } from "@/pages/admin/Kanban";
import { TicketsListPage } from "@/pages/admin/TicketsList";
import { TicketDetailPage } from "@/pages/admin/TicketDetail";
import { UsersPage } from "@/pages/admin/Users";
import { SettingsPage } from "@/pages/admin/Settings";
import { DepartmentDetailPage } from "@/pages/admin/DepartmentDetail";
import { TasksListPage } from "@/pages/admin/TasksList";
import { TasksWorkspacePage } from "@/pages/admin/TasksWorkspace";
import { TaskDetailPage } from "@/pages/admin/TaskDetail";
import { RequestsListPage } from "@/pages/admin/RequestsList";

import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<SubmitTicketPage />} />
              <Route path="/track" element={<TrackTicketPage />} />
              <Route path="/track/:token" element={<TrackTicketPage />} />
            </Route>

            {/* Auth */}
            <Route path="/admin/login" element={<LoginPage />} />
            <Route path="/admin/forget-password" element={<ForgetPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route 
              path="/admin/change-password" 
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              } 
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="kanban" element={<TasksWorkspacePage />} />
              <Route path="tickets" element={<TicketsListPage />} />
              <Route path="tickets/:id" element={<TicketDetailPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="tasks" element={<TasksListPage />} />
              <Route path="tasks/:id" element={<TaskDetailPage />} />
              <Route path="requests" element={<RequestsListPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="departments/:id" element={<DepartmentDetailPage />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;